import { Formik, Form, Field, ErrorMessage } from "formik";
import { motion, AnimatePresence } from "framer-motion";
import {
  XCircle,
  Send,
  AlertCircle,
  CircleAlert,
  MoveRight,
} from "lucide-react";
import { useState } from "react";
import { FiX } from "react-icons/fi";
import * as Yup from "yup";
import { deactivateSender } from "../../requests/Sender_Ids/SenderService";
import { getFormattedTimestamp } from "../../utils/Utils";

const DeclineSenderModal = ({
  isOpen,
  onClose,

  onSuccess,
  onError,
  senderId,
  sender,
  type,
}) => {
  const [reason, setFeedback] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = () => {};

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 z-[999] backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed inset-0 z-[1000] overflow-y-auto"
          >
            <div className="flex min-h-screen items-center justify-center p-4">
              <div className="w-full max-w-md">
                <motion.div
                  className="bg-white rounded-3xl shadow-[0_20px_70px_-10px_rgba(0,0,0,0.3)] overflow-hidden"
                  layoutId="modal-container"
                >
                  {/* Status Bar */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="w-full h-1.5 bg-gray-100"
                  >
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 1 }}
                      className="h-full bg-[#FF6C6C]"
                    />
                  </motion.div>
                  <Formik
                    initialValues={{
                      sender: sender?.sender || "",
                      status: "Rejected",
                      reject_reason: "",
                      rejected_date: getFormattedTimestamp(),
                    }}
                    validationSchema={Yup.object({
                      reject_reason: Yup.string()
                        .required("Please provide a reason before submmitting")
                        .min(10, "Must be at least 10 characters"),
                    })}
                    onSubmit={(values, actions) =>
                      deactivateSender(values, senderId, {
                        ...actions,
                        onSuccess,
                        onClose,
                      })
                    }
                  >
                    {({ isSubmitting,errors, touched }) => (
                      <>
                        {/* Header */}
                        <div className="px-8 pt-6 pb-4 border-b border-gray-100">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <motion.div
                                initial={{ rotate: 180, scale: 0 }}
                                animate={{ rotate: 0, scale: 1 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 25,
                                }}
                                className="bg-red-50 p-3 rounded-full"
                              >
                                {isSubmitting ? (
                                  <>
                                    <div className="relative inset-0 flex items-center justify-center">
                                      <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                  </>
                                ) : onSuccess ? (
                                  <CircleAlert
                                    className="h-6 w-6 text-[#FF6C6C]"
                                    strokeWidth={2.5}
                                  />
                                ) : (
                                  ""
                                )}
                              </motion.div>
                              <h2 className="text-xl font-bold text-gray-900">
                                {type === "decline"
                                  ? "Decline"
                                  : type === "suspend"
                                  ? "Suspend"
                                  : ""}{" "}
                                Sender ID
                              </h2>
                            </div>

                            <motion.button
                              whileHover={{ rotate: 90 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={onClose}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              <FiX className="h-6 w-6" />
                            </motion.button>
                          </div>
                        </div>
                        <p className=" px-8 py-6">
                          You are about to{" "}
                          {type === "decline"
                            ? "Decline"
                            : type === "suspend"
                            ? "Suspend"
                            : ""}{" "}
                          <b>{sender.sender}</b> as sender ID for{" "}
                          {sender.brand_name}{" "}
                        </p>

                        {/* Content */}

                        <Form>
                          <div className="px-8 py-6">
                            <div className="space-y-4">
                              <label className="block text-sm font-medium text-gray-700">
                                Reason for{" "}
                                {type === "decline"
                                  ? "Declining"
                                  : type === "suspend"
                                  ? "Suspending"
                                  : ""}
                              </label>
                              {/* <textarea
                              name="reason"
                              id="reason"
                              placeholder="Please provide detailed reason..."
                              className="w-full h-32 px-4 py-3 text-gray-700 border border-gray-200 
                                 rounded-xl resize-none focus:ring-2 focus:ring-[#FF6C6C] 
                                 focus:border-transparent outline-none transition-all"
                            /> */}

                              <div>
                                {/* <label htmlFor="description">Description</label> */}
                                <Field
                                  as="textarea"
                                  id="reject_reason"
                                  name="reject_reason"
                                  placeholder="Please provide detailed reason..."
                                  rows="5"
                                  className="w-full h-32 px-4 py-3 text-gray-700 border border-gray-200 
                                 rounded-xl resize-none focus:ring-2 focus:ring-[#FF6C6C] 
                                 focus:border-transparent outline-none transition-all"
                                />
                                {errors.reject_reason &&
                                  touched.reject_reason && (
                                    <motion.div
                                      initial={{ opacity: 0, y: -10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      className="flex items-center space-x-2 text-[#FF6C6C] text-sm mt-2"
                                    >
                                      <AlertCircle className="h-4 w-4" />
                                      <span>{errors.reject_reason}</span>
                                    </motion.div>
                                  )}
                              </div>
                            </div>
                          </div>

                          {/* Footer */}
                          <div className="px-8 py-4 bg-gray-50 flex justify-end space-x-3">
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={onClose}
                              className="px-6 py-2.5 text-sm font-semibold text-gray-700 
                               bg-white border border-gray-300 rounded-xl 
                               hover:bg-gray-50 transition-all duration-200"
                            >
                              Cancel
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              type="submit"
                              disabled={isSubmitting}
                              whileTap={{ scale: 0.98 }}
                              className="px-6 py-2.5 text-sm font-semibold text-white 
                               bg-[#FF6C6C] rounded-xl hover:bg-[#ff5555]
                               transition-all duration-200 flex items-center space-x-2"
                            >
                              <span>
                                {" "}
                                {type === "decline"
                                  ? "Decline"
                                  : type === "suspend"
                                  ? "Suspend"
                                  : ""}
                              </span>
                              <MoveRight className="h-4 w-4" />
                            </motion.button>
                          </div>
                        </Form>
                      </>
                    )}
                  </Formik>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DeclineSenderModal;
